import {
    Breadcrumb,
    DateField,
    EditButton,
    ReferenceField,
    Show,
    TextField,
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Calendar,
    CheckCircle,
    ClipboardCheck,
    Clock,
    Settings,
    Truck,
    User,
    XCircle,
} from 'lucide-react';
import { useNotify, useRecordContext, useRefresh, useUpdate } from 'ra-core';
import { Link, useNavigate } from 'react-router-dom';
import { StatusBadge } from '../documents/shared/StatusBadge';
import { WorkOrder } from '../types';

const ShowTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <>
            <Breadcrumb>
                <Breadcrumb.Item><Link to="/work_orders">Work Orders</Link></Breadcrumb.Item>
                <Breadcrumb.PageItem>{record.reference}</Breadcrumb.PageItem>
            </Breadcrumb>
            <span className="text-xl font-bold tracking-tight text-foreground">{record.reference}</span>
        </>
    );
};

const QA_Checklist = ({ workOrder }: { workOrder: WorkOrder }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [update] = useUpdate();

    const handleToggle = (index: number) => {
        const newCheckpoints = [...(workOrder.quality_checkpoints || [])];
        newCheckpoints[index].passed = !newCheckpoints[index].passed;
        
        update('work_orders', { id: workOrder.id, data: { quality_checkpoints: newCheckpoints }, previousData: workOrder }, {
            onSuccess: () => {
                notify('Checkpoint updated');
                refresh();
            }
        });
    };

    const checkpoints = workOrder.quality_checkpoints?.length ? workOrder.quality_checkpoints : [
        { task: 'Physical dimension check', passed: false, notes: '' },
        { task: 'Surface finish inspection', passed: false, notes: '' },
        { task: 'Electrical connectivity test (if applicable)', passed: false, notes: '' },
        { task: 'Packaging & Labelling verification', passed: false, notes: '' }
    ];

    return (
        <Card>
            <CardHeader className="pb-3 border-b">
                <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Quality Checklist</CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
                {checkpoints.map((cp, i) => (
                    <div key={cp.task} className="flex items-start gap-4 p-3 rounded-lg border border-border bg-card hover:bg-muted transition-colors">
                        <Checkbox 
                            id={`cp-${i}`} 
                            checked={!!cp.passed} 
                            onCheckedChange={() => handleToggle(i)} 
                            className="mt-1"
                        />
                        <div className="flex-1 space-y-1">
                            <label htmlFor={`cp-${i}`} className="text-sm font-semibold leading-none cursor-pointer">
                                {cp.task || `Checkpoint ${i + 1}`}
                            </label>
                            {cp.notes && <p className="text-xs text-muted-foreground">{cp.notes}</p>}
                        </div>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
};

const WorkOrderShowActions = () => {
    const record = useRecordContext<WorkOrder>();
    const navigate = useNavigate();
    const notify = useNotify();
    const refresh = useRefresh();
    const [update] = useUpdate();

    const handleComplete = () => {
        if (!record) return;
        if (record.qa_status !== 'passed') {
            notify('Quality Assurance must be passed before completing the Work Order', { type: 'warning' });
            return;
        }
        update('work_orders', { id: record.id, data: { status: 'completed', actual_completion: new Date().toISOString() }, previousData: record }, {
            onSuccess: () => {
                notify('Work Order completed');
                refresh();
            }
        });
    };

    const handleCreateDC = () => {
        if (!record) return;
        navigate('/delivery_challans/create', {
            state: {
                record: {
                    project_id: record.project_id,
                    status: 'draft',
                    challan_type: 'Job Work',
                }
            }
        });
    };

    return (
        <div className="flex items-center gap-2">
            <EditButton />
            {record?.status === 'in_progress' && (
                <Button
                    variant="outline"
                    size="sm"
                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                    onClick={handleComplete}
                >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Complete Work Order
                </Button>
            )}
            {record?.status === 'completed' && (
                <Button
                    variant="outline"
                    size="sm"
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={handleCreateDC}
                >
                    <Truck className="w-4 h-4 mr-2" />
                    Create DC
                </Button>
            )}
        </div>
    );
};

const WorkOrderShowContent = () => {
    const record = useRecordContext<WorkOrder>();
    const notify = useNotify();
    const refresh = useRefresh();
    const [update] = useUpdate();

    if (!record) return null;

    const handleUpdateQA = (newStatus: 'passed' | 'failed') => {
        update('work_orders', { id: record.id, data: { qa_status: newStatus }, previousData: record }, {
            onSuccess: () => {
                notify(`QA status updated to ${newStatus}`);
                refresh();
            }
        });
    };

    const priorityColors: Record<string, string> = {
        low: 'bg-muted text-muted-foreground border-border',
        medium: 'bg-blue-50 text-blue-700 border-blue-200',
        high: 'bg-orange-50 text-orange-700 border-orange-200',
        urgent: 'bg-red-50 text-red-700 border-red-200',
    };

    const qaColors: Record<string, { icon: any; color: string }> = {
        pending: { icon: Clock, color: 'text-amber-600' },
        passed: { icon: CheckCircle, color: 'text-emerald-600' },
        failed: { icon: XCircle, color: 'text-red-600' },
    };
    const QAIconComponent = qaColors[record.qa_status || 'pending']?.icon || Clock;

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-8">
            {/* Header */}
            <div className="flex justify-between items-start border-b pb-8">
                <div className="flex gap-6 items-center">
                    <div className="w-16 h-16 bg-orange-50 rounded-xl flex items-center justify-center border-2 border-orange-100 shadow-sm">
                        <Settings className="w-8 h-8 text-orange-500" />
                    </div>
                    <div className="space-y-1">
                        <div className="flex items-center gap-3">
                            <h2 className="text-3xl font-bold tracking-tight text-foreground">{record.reference}</h2>
                            <StatusBadge status={record.status} />
                            <Badge variant="outline" className={`text-[10px] font-bold uppercase ${priorityColors[record.priority]}`}>
                                {record.priority}
                            </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <ReferenceField source="project_id" reference="projects" link="show">
                                <TextField source="name" className="text-primary hover:underline font-medium" />
                            </ReferenceField>
                        </div>
                    </div>
                </div>
            </div>

            <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid w-full max-w-md grid-cols-2 mb-8">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="qa">Quality Assurance</TabsTrigger>
                </TabsList>

                <TabsContent value="overview">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="md:col-span-2 space-y-6">
                            <Card>
                                <CardHeader className="pb-3 border-b">
                                    <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                        <Calendar className="w-3.5 h-3.5" /> Schedule
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="pt-6">
                                    <div className="grid grid-cols-3 gap-6">
                                        <div className="space-y-1">
                                            <span className="text-[10px] font-bold text-muted-foreground uppercase">Start Date</span>
                                            <div className="text-sm font-semibold"><DateField source="start_date" /></div>
                                        </div>
                                        <div className="space-y-1">
                                            <span className="text-[10px] font-bold text-muted-foreground uppercase">Target Completion</span>
                                            {/* @ts-ignore */}
                                            <div className="text-sm font-semibold"><DateField source="target_completion" emptyText="Not set" /></div>
                                        </div>
                                        <div className="space-y-1">
                                            <span className="text-[10px] font-bold text-muted-foreground uppercase">Actual Completion</span>
                                            {/* @ts-ignore */}
                                            <div className="text-sm font-semibold"><DateField source="actual_completion" emptyText="In progress" /></div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {record.production_specs && (
                                <Card>
                                    <CardHeader className="pb-3 border-b">
                                        <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                            <Settings className="w-3.5 h-3.5" /> Production Specifications
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="pt-4 text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                                        {record.production_specs}
                                    </CardContent>
                                </Card>
                            )}
                        </div>

                        <div className="space-y-6">
                             <Card className="bg-muted border-none shadow-none">
                                <CardHeader>
                                    <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Assignment</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-card rounded-full flex items-center justify-center border shadow-sm">
                                            <User className="w-5 h-5 text-muted-foreground" />
                                        </div>
                                        <div>
                                            <span className="text-[10px] font-bold text-muted-foreground uppercase block">Assigned To</span>
                                            {/* @ts-ignore */}
                                            <ReferenceField source="assigned_to" reference="sales" link="show" emptyText="Unassigned">
                                                <TextField source="first_name" className="font-bold text-sm" />
                                            </ReferenceField>
                                        </div>
                                    </div>
                                    {record.bom_id && (
                                        <div className="pt-4 border-t border-border">
                                            <span className="text-[10px] font-bold text-muted-foreground uppercase block mb-1">Linked BoM</span>
                                            <ReferenceField source="bom_id" reference="boms" link="show">
                                                <TextField source="reference" className="text-blue-600 font-bold text-sm" />
                                            </ReferenceField>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="pb-3 border-b">
                                    <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Production Milestones</CardTitle>
                                </CardHeader>
                                <CardContent className="pt-4 space-y-4">
                                    {(record.milestones || [
                                        { name: 'Material Requisition', completed: true, date: record.created_at },
                                        { name: 'Production Start', completed: record.status !== 'open', date: record.start_date },
                                        { name: 'Quality Inspection', completed: record.qa_status === 'passed', date: null },
                                        { name: 'Final Assembly', completed: false, date: null }
                                    ]).map((m, i) => (
                                        <div key={i} className="flex gap-3 items-start relative">
                                            {i < 3 && <div className="absolute left-[9px] top-6 bottom-[-20px] w-0.5 bg-muted" />}
                                            <div className={`mt-1.5 w-5 h-5 rounded-full border-2 flex items-center justify-center z-10 ${m.completed ? 'bg-emerald-500 border-emerald-500' : 'bg-card border-border'}`}>
                                                {m.completed && <CheckCircle className="w-3 h-3 text-white" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm font-semibold truncate ${m.completed ? 'text-foreground' : 'text-muted-foreground'}`}>{m.name}</p>
                                                {m.date && <p className="text-[10px] text-muted-foreground mt-0.5">{new Date(m.date).toLocaleDateString()}</p>}
                                            </div>
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="qa">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="md:col-span-2">
                             <QA_Checklist workOrder={record} />
                        </div>
                        <div className="space-y-6">
                            <Card>
                                <CardHeader className="pb-3 border-b">
                                    <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                                        <ClipboardCheck className="w-3.5 h-3.5" /> Overall Status
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="py-8 flex flex-col items-center text-center space-y-4">
                                    <div className={`w-16 h-16 rounded-full flex items-center justify-center ${record.qa_status === 'passed' ? 'bg-emerald-50' : record.qa_status === 'failed' ? 'bg-red-50' : 'bg-amber-50'}`}>
                                        <QAIconComponent className={`w-8 h-8 ${qaColors[record.qa_status || 'pending']?.color}`} />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold capitalize">{record.qa_status || 'Pending'}</h3>
                                    </div>
                                    <div className="w-full pt-4 border-t flex flex-col gap-2">
                                        <Button 
                                            size="sm" 
                                            variant="outline" 
                                            className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                            disabled={record.qa_status === 'passed'}
                                            onClick={() => handleUpdateQA('passed')}
                                        >Pass Inspection</Button>
                                        <Button 
                                            size="sm" 
                                            variant="outline" 
                                            className="w-full border-red-200 text-red-700 hover:bg-red-50"
                                            disabled={record.qa_status === 'failed'}
                                            onClick={() => handleUpdateQA('failed')}
                                        >Flag Issues</Button>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export const WorkOrderShow = () => (
    <Show title={<ShowTitle />} actions={<WorkOrderShowActions />} disableBreadcrumb>
        <WorkOrderShowContent />
    </Show>
);
